/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Box, Button, CircularProgress, IconButton, InputAdornment, styled } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { FORGETPASSWORD, verifyResetPasswordLink } from "../../gql/mutations/auth";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import { regexPassword } from "../../utils/validations";
import { usePopup } from "../../context/commonPop";
import CustomTextField from "../forms/theme-elements/CustomTextField";

import ResetVerificationPopup from "../Modal/resetVerification";
const PassWordShowIconWrapper = styled(InputAdornment)(({ theme }) => ({
  "& button": {
    "&:focus": {
      outline: "none",
    },
  },
}));

function AuthForgotPassword() {
  const [forgetPassword] = useMutation(FORGETPASSWORD);
  const navigate = useNavigate();
  const { token } = useParams();
  const [resetVerification] = useLazyQuery(verifyResetPasswordLink, { fetchPolicy: "network-only" });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { handleOpen } = usePopup();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .matches(regexPassword, "Password must contain at least one uppercase letter, one lowercase letter, one digit, one special character, and be 8-15 characters long")
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .max(15, "Password must be at most 15 characters long"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const handleSubmit = values => {
    setIsLoading(true);
    forgetPassword({
      variables: {
        data: { token, password: values?.password },
      },
    })
      .then(() => {
        handleOpen(true, "Password Reset Successfully");
        setErrorMessage(null);
        navigate("/");
      })
      .catch(error => {
        setIsLoading(false);
        setErrorMessage(error.message || "Server Error");
      });
  };

  const handleVerification = async () => {
    resetVerification({
      variables: {
        token,
      },
      onCompleted: data => {
        console.log("data", data);
      },
      onError: errdata => {
        setOpen(true);
        setMessage(errdata?.message || "something went wrong");
      },
    });
  };

  useEffect(() => {
    handleVerification();
  }, []);

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ values, handleChange }) => (
        <Form className="resetForm">
          {open && <ResetVerificationPopup handleCloseAuth message={message} open={open} setOpen={setOpen} />}
          <Box>
            <CustomFormLabel htmlFor="password">New Password</CustomFormLabel>
            <CustomTextField
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              sx={{ width: "100%" }}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <PassWordShowIconWrapper position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </PassWordShowIconWrapper>
                ),
              }}
            />
            <ErrorMessage name="password" component="div" className="error" />
          </Box>

          <Box>
            <CustomFormLabel htmlFor="confirmPassword">Confirm New Password</CustomFormLabel>
            <CustomTextField
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={values.confirmPassword}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <PassWordShowIconWrapper position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </PassWordShowIconWrapper>
                ),
              }}
            />
            <ErrorMessage name="confirmPassword" component="div" className="error" />
          </Box>

          {errorMessage && (
            <Box component="p" sx={{ marginTop: "15px", color: "red", fontWeight: "600", marginBottom: 0 }}>
              {errorMessage}
            </Box>
          )}

          <Button className="resetBtn" type="submit" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : "Reset"}
          </Button>
        </Form>
      )}
    </Formik>
  );
}

export default AuthForgotPassword;
